import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que la pantalla de personal le pide a la API (#58), con la forma del
// `datos.ts` de proveedores: clave de caché con el taller adentro, una consulta
// por lectura y una función por escritura que devuelve el `ProblemDetails` o nada.
//
// Lo propio de personal: el código de empleado no se edita con la ficha. Es el
// usuario para entrar al sistema, y la API lo cambia por su propio camino
// (`/employee-code`), que renombra el usuario de Keycloak por la cola (#173).

export type Persona = components['schemas']['StaffListItem'];
export type FichaPersona = components['schemas']['StaffDetail'];

/** El 409 de la baja (#163): en cuántas órdenes aparece y si sigue activa. */
export type ProblemaBajaPersona = ProblemaApi & { orderCount?: number | string | null; isActive?: boolean | null };

/// Lo que edita el formulario. Los nombres son los del contrato: los errores por
/// campo de la API llegan con esas claves. `employeeCode` solo cuenta en el alta;
/// vacío deja que lo proponga el correlativo del taller.
export interface ValoresPersona {
  employeeCode: string;
  firstName: string;
  lastName: string;
  hiredOn: string;
  phone: string;
  email: string;
  specialties: string[];
  locations: string[];
}

export interface FiltrosPersonal {
  buscar: string;
  especialidad: string;
  sucursal: string;
  estado: 'todos' | 'activos' | 'inactivos';
  cursor: string | null;
}

/// El taller de la pestaña (#205): va en toda clave de caché (regla 5 de multitenancy,
/// en el navegador). Ver `sucursales/datos.ts`.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  // `['personal', taller]` es también el prefijo de la lista que usa «Asignar
  // personal» en sucursales: guardar aquí la refresca allá.
  todo: (taller: string) => ['personal', taller] as const,
  lista: (taller: string, filtros: FiltrosPersonal) => ['personal', taller, 'lista', filtros] as const,
  ficha: (taller: string, id: string) => ['personal', taller, 'ficha', id] as const,
  // Fuera de `personal`, como en proveedores: volver a pedir el código justo antes
  // de salir del alta sería una consulta para nadie.
  siguienteCodigo: (taller: string) => ['siguienteCodigoPersona', taller] as const,
  especialidades: (taller: string) => ['personal', taller, 'especialidades'] as const,
  // Bajo `sucursales`: crear, editar o desactivar una sucursal ya invalida ese
  // prefijo, y esta lista se entera sola.
  sucursales: (taller: string) => ['sucursales', taller, 'todas'] as const,
};

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

/** «Carlos Méndez». */
export const nombreDe = (p: Pick<Persona, 'firstName' | 'lastName'>) => `${p.firstName} ${p.lastName}`;

/** Espacios afuera, y vacío pasa a `null`: la API guarda «sin teléfono», no un texto vacío. */
const opcional = (texto: string) => texto.trim() || null;

/** Lo común del alta y la edición. */
function cuerpo(v: ValoresPersona) {
  return {
    firstName: v.firstName.trim(),
    lastName: v.lastName.trim(),
    email: opcional(v.email),
    phone: opcional(v.phone),
    hiredOn: v.hiredOn || null,
    specialties: v.specialties,
    locations: v.locations,
  };
}

export function usePersonal(filtros: FiltrosPersonal) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.lista(taller, filtros),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/staff', {
        params: {
          query: {
            ...(filtros.buscar.trim() && { search: filtros.buscar.trim() }),
            ...(filtros.estado !== 'todos' && { isActive: filtros.estado === 'activos' }),
            ...(filtros.especialidad && { specialty: filtros.especialidad }),
            ...(filtros.sucursal && { location: filtros.sucursal }),
            ...(filtros.cursor && { cursor: filtros.cursor }),
          },
        },
      });
      if (error || !data) throw new Error('No se pudo leer la lista del personal');
      return data;
    },
    // Mientras llega la página siguiente se sigue viendo la anterior: sin esto la
    // tabla parpadea a «Cargando…» en cada tecla del buscador.
    placeholderData: keepPreviousData,
  });
}

export function useFichaPersona(id: string | undefined) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ficha(taller, id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/staff/{id}', {
        params: { path: { id: id ?? '' } },
      });
      // El 404 no es un fallo de red: la persona no existe para este taller.
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer la ficha de la persona');
      return data;
    },
    // Siempre fresca al abrir la edición: con la versión vieja en caché, guardar
    // chocaría contra la edición de otro que ya estaba en la base.
    staleTime: 0,
  });
}

/**
 * Las especialidades activas del taller, por nombre: alimentan las sugerencias
 * del campo y el filtro del listado. Se recorren las páginas: un taller tiene un
 * puñado, no cientos.
 */
export function useEspecialidades() {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.especialidades(taller),
    queryFn: async () => {
      const todas: components['schemas']['SpecialtyListItem'][] = [];
      let cursor: string | undefined;
      do {
        const { data, error } = await api.GET('/api/specialties', {
          params: { query: { limit: 100, ...(cursor && { cursor }) } },
        });
        if (error || !data) throw new Error('No se pudieron leer las especialidades');
        todas.push(...data.items);
        cursor = data.hasMore && data.nextCursor ? data.nextCursor : undefined;
      } while (cursor);
      return todas;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Todas las sucursales del taller, activas o no: las activas se ofrecen en el
 * formulario, y todas sirven para poner nombre a lo que ya tiene asignado cada
 * persona. Piden `settings.edit`; sin él, la consulta falla y la pantalla sigue
 * sin sucursales.
 */
export function useSucursalesDelTaller() {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.sucursales(taller),
    queryFn: async () => {
      const todas: components['schemas']['LocationListItem'][] = [];
      let cursor: string | undefined;
      do {
        const { data, error } = await api.GET('/api/locations', {
          params: { query: { limit: 100, ...(cursor && { cursor }) } },
        });
        if (error || !data) throw new Error('No se pudieron leer las sucursales');
        todas.push(...data.items);
        cursor = data.hasMore && data.nextCursor ? data.nextCursor : undefined;
      } while (cursor);
      return todas;
    },
    staleTime: 60 * 1000,
    retry: false,
  });
}

/**
 * El código que tendría la próxima persona, para mostrarlo ya puesto en el alta.
 * No reserva nada: el alta lo manda vacío si nadie lo tocó. Sin caché: uno
 * guardado de la visita anterior ya lo habría usado otra persona.
 */
export function useSiguienteCodigo(habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.siguienteCodigo(taller),
    enabled: habilitado,
    queryFn: pedirSiguienteCodigo,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** Otro código propuesto, o vacío si no se pudo saber: el alta sigue sin él. */
export async function pedirSiguienteCodigo(): Promise<string> {
  try {
    const { data } = await api.GET('/api/staff/next-code');
    return data?.code ?? '';
  } catch {
    return '';
  }
}

/** Las escrituras. Cada una refresca al personal del taller al terminar bien. */
export function useCambiosPersona() {
  const taller = useTaller();
  const cache = useQueryClient();

  // Las especialidades también: el alta y la edición crean las nuevas al vuelo.
  const refrescar = () => cache.invalidateQueries({ queryKey: claves.todo(taller) });

  /** Con el cuerpo vacío —un 502 del proxy— `error` llega como `""`: falso, pero es un error. */
  const problemaDe = (error: unknown) => (typeof error === 'object' && error ? error : {}) as ProblemaApi;

  /**
   * `codigoPropuesto` es el que se mostró ya puesto: si sigue igual, se manda
   * vacío y lo asigna el servidor, para que dos altas simultáneas no choquen por
   * el mismo número.
   */
  async function crear(
    v: ValoresPersona,
    codigoPropuesto = '',
  ): Promise<{ codigo?: string; problema?: ProblemaApi }> {
    const codigo = v.employeeCode.trim();
    const { data, error } = await api.POST('/api/staff', {
      body: { ...cuerpo(v), employeeCode: codigo && codigo !== codigoPropuesto ? codigo : null },
    });
    if (error !== undefined || !data) return { problema: problemaDe(error) };
    await refrescar();
    return { codigo: data.employeeCode };
  }

  async function editar(id: string, versionActual: number, v: ValoresPersona): Promise<ProblemaApi | undefined> {
    const { error } = await api.PUT('/api/staff/{id}', {
      params: { path: { id } },
      body: { version: versionActual, ...cuerpo(v) },
    });
    if (error !== undefined) return problemaDe(error);
    await refrescar();
    return undefined;
  }

  /** Renombra su usuario de Keycloak por la cola: si Keycloak no responde, llega después. */
  async function cambiarCodigo(id: string, codigo: string): Promise<ProblemaApi | undefined> {
    const { error } = await api.PUT('/api/staff/{id}/employee-code', {
      params: { path: { id } },
      body: { employeeCode: codigo.trim() },
    });
    if (error !== undefined) return problemaDe(error);
    await refrescar();
    return undefined;
  }

  async function cambiarEstado(id: string, activo: boolean): Promise<ProblemaApi | undefined> {
    const { error } = await api.PUT('/api/staff/{id}/status', {
      params: { path: { id } },
      body: { isActive: activo },
    });
    if (error !== undefined) return problemaDe(error);
    await refrescar();
    return undefined;
  }

  /** Baja lógica. Con trabajo registrado la API no la deja y dice por qué (#163). */
  async function eliminar(id: string): Promise<ProblemaBajaPersona | undefined> {
    const { error } = await api.DELETE('/api/staff/{id}', { params: { path: { id } } });
    if (error !== undefined) return problemaDe(error) as ProblemaBajaPersona;
    // Todo menos la ficha que se acaba de eliminar: pedirla de nuevo daría un 404
    // justo mientras la pantalla abierta sale al listado.
    const eliminada = claves.ficha(taller, id);
    await cache.invalidateQueries({
      queryKey: claves.todo(taller),
      predicate: (consulta) => !eliminada.every((parte, i) => consulta.queryKey[i] === parte),
    });
    return undefined;
  }

  return { crear, editar, cambiarCodigo, cambiarEstado, eliminar, refrescar };
}
